/**
 * cdn-domain-security-headers-tab.tsx — Security Headers tab for a CDN domain (act-as).
 *
 * HSTS, CSP, X-Frame-Options, X-Content-Type-Options, X-XSS-Protection,
 * Referrer-Policy, Permissions-Policy.
 *
 * Endpoints: GET/PUT /api/v3/cdn/domains/:id/security-headers
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, Input, Switch, Skeleton, Textarea,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNSecurityHeaders,
  actAsCDNSecurityKeys,
  type SecurityHeadersConfig,
} from '@/api/act-as-cdn-security';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

interface SecForm {
  hsts_enabled: boolean;
  hsts_max_age: string;
  hsts_subdomains: boolean;
  hsts_preload: boolean;
  csp_enabled: boolean;
  csp_policy: string;
  x_frame_options: 'DENY' | 'SAMEORIGIN' | 'disabled';
  x_content_type_options: boolean;
  x_xss_protection: boolean;
  referrer_policy: string;
  permissions_policy: string;
}

const DEFAULTS: SecForm = {
  hsts_enabled: false,
  hsts_max_age: '31536000',
  hsts_subdomains: false,
  hsts_preload: false,
  csp_enabled: false,
  csp_policy: '',
  x_frame_options: 'SAMEORIGIN',
  x_content_type_options: true,
  x_xss_protection: true,
  referrer_policy: 'strict-origin-when-cross-origin',
  permissions_policy: '',
};

const REFERRER_POLICIES = [
  'no-referrer',
  'no-referrer-when-downgrade',
  'origin',
  'origin-when-cross-origin',
  'same-origin',
  'strict-origin',
  'strict-origin-when-cross-origin',
  'unsafe-url',
];

function hydrate(cfg: SecurityHeadersConfig): SecForm {
  return {
    hsts_enabled: cfg.hsts_enabled ?? false,
    hsts_max_age: String(cfg.hsts_max_age ?? 31536000),
    hsts_subdomains: cfg.hsts_subdomains ?? false,
    hsts_preload: cfg.hsts_preload ?? false,
    csp_enabled: cfg.csp_enabled ?? false,
    csp_policy: cfg.csp_policy ?? '',
    x_frame_options: cfg.x_frame_options ?? 'SAMEORIGIN',
    x_content_type_options: cfg.x_content_type_options ?? true,
    x_xss_protection: cfg.x_xss_protection ?? true,
    referrer_policy: cfg.referrer_policy ?? 'strict-origin-when-cross-origin',
    permissions_policy: cfg.permissions_policy ?? '',
  };
}

function toPayload(f: SecForm): SecurityHeadersConfig {
  return {
    hsts_enabled: f.hsts_enabled,
    hsts_max_age: Number(f.hsts_max_age) || 0,
    hsts_subdomains: f.hsts_subdomains,
    hsts_preload: f.hsts_preload,
    csp_enabled: f.csp_enabled,
    csp_policy: f.csp_policy,
    x_frame_options: f.x_frame_options,
    x_content_type_options: f.x_content_type_options,
    x_xss_protection: f.x_xss_protection,
    referrer_policy: f.referrer_policy,
    permissions_policy: f.permissions_policy,
  };
}

interface Props { domainId: string }

export function CdnDomainSecurityHeadersTab({ domainId }: Props) {
  const { t } = useTranslation();
  const q = useActAsCDNSecurityHeaders(domainId);
  const [form, setForm] = useState<SecForm>(DEFAULTS);
  const [snapshot, setSnapshot] = useState<SecForm>(DEFAULTS);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const next = q.data ? hydrate(q.data) : DEFAULTS;
    setForm(next); setSnapshot(next);
  }, [q.data]);

  const dirty = JSON.stringify(toPayload(form)) !== JSON.stringify(toPayload(snapshot));
  const set = <K extends keyof SecForm>(k: K, v: SecForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  if (q.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (q.isError) {
    return <TabError title={t('cdn_domain.sec_headers_tab.load_error', 'Failed to load security headers')} onRetry={() => q.refetch()} />;
  }

  return (
    <Section
      title={t('cdn_domain.sec_headers_tab.title', 'Security Headers')}
      description={t('cdn_domain.sec_headers_tab.description', 'HTTP security response headers injected by the CDN.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

        {/* HSTS */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.sec_headers_tab.section_hsts', 'HSTS')}</CardTitle>
            <CardDescription>{t('cdn_domain.sec_headers_tab.section_hsts_desc', 'Strict-Transport-Security header — forces HTTPS.')}</CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            <SettingRow
              title={t('cdn_domain.sec_headers_tab.field_hsts_enabled', 'Enable HSTS')}
              control={<Switch checked={form.hsts_enabled} onCheckedChange={(v) => set('hsts_enabled', v)} />}
            />
            {form.hsts_enabled && (
              <>
                <div style={{ marginTop: 'var(--vn-space-3)', maxWidth: 220 }}>
                  <Field label={t('cdn_domain.sec_headers_tab.field_hsts_max_age', 'Max age (seconds)')}>
                    {(p) => (
                      <NumericInput {...p} value={form.hsts_max_age}
                        onChange={(e) => set('hsts_max_age', e.target.value)} />
                    )}
                  </Field>
                </div>
                <SettingRow
                  title={t('cdn_domain.sec_headers_tab.field_hsts_subdomains', 'Include subdomains')}
                  control={<Switch checked={form.hsts_subdomains} onCheckedChange={(v) => set('hsts_subdomains', v)} />}
                />
                <SettingRow
                  title={t('cdn_domain.sec_headers_tab.field_hsts_preload', 'Preload')}
                  description={t('cdn_domain.sec_headers_tab.field_hsts_preload_desc', 'Submit to HSTS preload list. Requires includeSubDomains and max-age ≥ 31536000.')}
                  control={<Switch checked={form.hsts_preload} onCheckedChange={(v) => set('hsts_preload', v)} />}
                />
              </>
            )}
          </CardBody>
        </Card>

        {/* CSP */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.sec_headers_tab.section_csp', 'Content Security Policy')}</CardTitle>
            <CardDescription>{t('cdn_domain.sec_headers_tab.section_csp_desc', 'Content-Security-Policy header value.')}</CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <SettingRow
              title={t('cdn_domain.sec_headers_tab.field_csp_enabled', 'Enable CSP')}
              control={<Switch checked={form.csp_enabled} onCheckedChange={(v) => set('csp_enabled', v)} />}
            />
            {form.csp_enabled && (
              <Field label={t('cdn_domain.sec_headers_tab.field_csp_policy', 'CSP policy')}
                description={t('cdn_domain.sec_headers_tab.field_csp_policy_desc', 'Full CSP directive string.')}>
                {(p) => (
                  <Textarea {...p} rows={4} value={form.csp_policy}
                    placeholder="default-src 'self'; script-src 'self' 'unsafe-inline';"
                    onChange={(e) => set('csp_policy', e.target.value)} />
                )}
              </Field>
            )}
          </CardBody>
        </Card>

        {/* Other headers */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.sec_headers_tab.section_other', 'Other Headers')}</CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <SettingRow
              title="X-Frame-Options"
              description={t('cdn_domain.sec_headers_tab.field_xfo_desc', 'Controls whether the page can be embedded in a frame.')}
              control={
                <Select value={form.x_frame_options} onValueChange={(v) => set('x_frame_options', v as SecForm['x_frame_options'])}>
                  <SelectTrigger style={{ width: 160 }}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DENY">DENY</SelectItem>
                    <SelectItem value="SAMEORIGIN">SAMEORIGIN</SelectItem>
                    <SelectItem value="disabled">{t('cdn_domain.sec_headers_tab.disabled', 'Disabled')}</SelectItem>
                  </SelectContent>
                </Select>
              }
            />
            <SettingRow
              title="X-Content-Type-Options"
              description={t('cdn_domain.sec_headers_tab.field_xcto_desc', 'Prevents MIME sniffing (nosniff).')}
              control={<Switch checked={form.x_content_type_options} onCheckedChange={(v) => set('x_content_type_options', v)} />}
            />
            <SettingRow
              title="X-XSS-Protection"
              description={t('cdn_domain.sec_headers_tab.field_xxss_desc', 'Legacy XSS filter for older browsers.')}
              control={<Switch checked={form.x_xss_protection} onCheckedChange={(v) => set('x_xss_protection', v)} />}
            />
            <div style={{ maxWidth: 320 }}>
              <Field label="Referrer-Policy">
                {(p) => (
                  <Select value={form.referrer_policy} onValueChange={(v) => set('referrer_policy', v)}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REFERRER_POLICIES.map((rp) => <SelectItem key={rp} value={rp}>{rp}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
            <Field label="Permissions-Policy"
              description={t('cdn_domain.sec_headers_tab.field_pp_desc', 'Feature policy directives. Leave empty to omit header.')}>
              {(p) => (
                <Input {...p} value={form.permissions_policy}
                  placeholder="camera=(), microphone=(), geolocation=()"
                  onChange={(e) => set('permissions_policy', e.target.value)} />
              )}
            </Field>
          </CardBody>
        </Card>

        <ActionBar
          end={
            <>
              <Button variant="secondary" disabled={!dirty} onClick={() => setForm(snapshot)}>{t('common.cancel', 'Cancel')}</Button>
              <Button disabled={!dirty} onClick={() => setConfirmOpen(true)}>{t('cdn_domain.sec_headers_tab.action_save', 'Save')}</Button>
            </>
          }
        />
      </div>

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.sec_headers_tab.confirm_save_title', 'Save security headers')}
        summary={t('cdn_domain.sec_headers_tab.save_summary', 'Update security response headers (HSTS, CSP, X-Frame-Options, etc.) for this domain.')}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.securityHeaders(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/security-headers`, toPayload(form))}
        onSuccess={() => setSnapshot(form)}
      />
    </Section>
  );
}
